import { z } from 'zod';
import { CadFingerprintSchema } from './cadImport.js';
import { SEMVER_RE } from './semver.js';
import { OptomechCitationSchema } from './optomechMaterials.js';

/**
 * Optomech parts catalog — spec
 * docs/superpowers/specs/2026-10-19-optomech-catalog-composition-design.md
 * §4.1–§4.3 (migration 125).
 *
 * The catalog holds DIMENSIONS, not shapes: a row of a standard family is
 * built into a solid by fea-service's generator for that family
 * (fea-service/fea/parts.py), so every number here is one the generator or a
 * fit rule reads. Lengths are millimetres, the lens units of the rest of the
 * optomech contract; an inch drawing is converted by the seed compiler and the
 * conversion is recorded in the dimension's citation.
 *
 * Each family schema is split into GENERATOR INPUTS (required: a row missing
 * one cannot be built, and a row that cannot be built is not a part) and FIT
 * DATA (optional: the row loads, and a fit rule that needs the field refuses
 * by name). The split is the schema's required/optional, not a side list.
 */

export const OPTOMECH_PART_FAMILIES = ['lens_tube', 'retaining_ring', 'spacer_ring', 'cell', 'adapter', 'other'] as const;
export const OptomechPartFamilySchema = z.enum(OPTOMECH_PART_FAMILIES);
export type OptomechPartFamily = z.infer<typeof OptomechPartFamilySchema>;

/** The families a generator exists for; the others load as rows but cannot be composed yet. */
export const OPTOMECH_GENERATED_FAMILIES = ['lens_tube', 'retaining_ring', 'spacer_ring'] as const;
export type OptomechGeneratedFamily = typeof OPTOMECH_GENERATED_FAMILIES[number];

export const OPTOMECH_PART_SCOPES = ['global', 'project'] as const;
export const OptomechPartScopeSchema = z.enum(OPTOMECH_PART_SCOPES);

/** A thread key as the vendor names the standard: SM05, SM1, SM1.5, SM2, M25.5x0.5 as they arrive. */
export const OPTOMECH_THREAD_KEY_RE = /^[A-Za-z0-9.]{1,20}$/;
export const OptomechThreadKeySchema = z.string().regex(OPTOMECH_THREAD_KEY_RE);

/** One thread standard (optomech_threads). Fit compares `key`; the diameters model the interface and sanity-check seeds. */
export const OptomechThreadSchema = z.object({
  key: OptomechThreadKeySchema,
  major_diameter_mm: z.number().positive(),
  pitch_mm: z.number().positive(),
  /** The internal thread's minimum minor diameter: the one cylinder every threaded interface is modelled at (spec §7, §11 Q2). */
  interface_diameter_mm: z.number().positive(),
  /** 60° unified form, or metric. */
  form: z.enum(['UN', 'M']),
  /** The vendor's callout as written, e.g. 1.035"-40. */
  callout: z.string().min(1).max(60),
  source: z.string().min(1),
});
export type OptomechThread = z.infer<typeof OptomechThreadSchema>;

const mm = z.number().positive().finite();

/**
 * A lens tube: a cylinder with a bore the optic drops into, internally
 * threaded. The generator models the threaded bore as the thread's
 * MINOR-diameter cylinder, which is `bore_mm` (spec §7, §11 Q2): a retaining
 * ring generated at the same diameter then shares that cylinder with the tube
 * in the mesh.
 */
export const OptomechLensTubeInterfaceSchema = z.object({
  // generator inputs
  thread: OptomechThreadKeySchema,
  bore_mm: mm,
  outer_diameter_mm: mm,
  length_mm: mm,
  // fit data
  /** Internally threaded length measured from each end; absent when the drawing does not state it. */
  internal_thread_length_mm: z.object({ front: mm.optional(), rear: mm.optional() }).strict().optional(),
  /** An external thread on one end, modelled as a stub of `diameter_mm` over `length_mm`. The stub takes the thread's MAJOR diameter, not the interface diameter the bore takes: a tube threaded inside and outside at one standard would otherwise have no wall there. Changes the solid when present. */
  external_thread: z.object({ thread: OptomechThreadKeySchema, diameter_mm: mm, length_mm: mm, end: z.enum(['front', 'rear']) }).strict().optional(),
  /** An internal stop the optic can seat on: its rear face's position from the tube's rear face, its thickness and its inner diameter. Changes the solid when present. */
  shoulder: z.object({ from_rear_mm: z.number().nonnegative().finite(), thickness_mm: mm, inner_diameter_mm: mm }).strict().optional(),
}).strict().refine((t) => t.bore_mm < t.outer_diameter_mm, { message: 'bore_mm must be smaller than outer_diameter_mm' })
  .refine((t) => t.shoulder === undefined || (t.shoulder.from_rear_mm + t.shoulder.thickness_mm <= t.length_mm && t.shoulder.inner_diameter_mm < t.bore_mm), { message: 'a shoulder lies inside the tube and inside the bore' })
  .refine((t) => t.external_thread === undefined || (t.external_thread.length_mm < t.length_mm && t.external_thread.diameter_mm > t.bore_mm && t.external_thread.diameter_mm < t.outer_diameter_mm), { message: 'an external thread stub is shorter than the tube and lies between the bore and the outer diameter' });
export type OptomechLensTubeInterface = z.infer<typeof OptomechLensTubeInterfaceSchema>;

/**
 * A retaining ring: an annulus that threads into a tube and bears on the
 * optic with one face. `outer_diameter_mm` is the thread's minor diameter as
 * the drawing gives it, i.e. the bore of the tube it threads into.
 */
export const OptomechRetainingRingInterfaceSchema = z.object({
  // generator inputs
  thread: OptomechThreadKeySchema,
  outer_diameter_mm: mm,
  thickness_mm: mm,
  clear_aperture_mm: mm,
  // fit data
  /** Which face bears on the optic, and the chamfer angle of that face when it is not flat. */
  seat: z.object({ face: z.enum(['front', 'rear']), chamfer_deg: z.number().positive().lt(90).optional() }).strict().optional(),
}).strict().refine((r) => r.clear_aperture_mm < r.outer_diameter_mm, { message: 'clear_aperture_mm must be smaller than outer_diameter_mm' });
export type OptomechRetainingRingInterface = z.infer<typeof OptomechRetainingRingInterfaceSchema>;

/** A spacer ring: an annulus that rides in a tube between two optics. */
export const OptomechSpacerRingInterfaceSchema = z.object({
  // generator inputs
  outer_diameter_mm: mm,
  inner_diameter_mm: mm,
  length_mm: mm,
  // fit data
  seats: z.object({
    front: z.object({ chamfer_deg: z.number().positive().lt(90).optional() }).strict().optional(),
    rear: z.object({ chamfer_deg: z.number().positive().lt(90).optional() }).strict().optional(),
  }).strict().optional(),
}).strict().refine((s) => s.inner_diameter_mm < s.outer_diameter_mm, { message: 'inner_diameter_mm must be smaller than outer_diameter_mm' });
export type OptomechSpacerRingInterface = z.infer<typeof OptomechSpacerRingInterfaceSchema>;

/** The dimension record of each generated family, by family. */
export const OPTOMECH_FAMILY_INTERFACE = {
  lens_tube: OptomechLensTubeInterfaceSchema,
  retaining_ring: OptomechRetainingRingInterfaceSchema,
  spacer_ring: OptomechSpacerRingInterfaceSchema,
} as const satisfies Record<OptomechGeneratedFamily, z.ZodTypeAny>;

/** The record of a family without a generator (cell, adapter, other): the seed's dimensions as given, no field read by any rule yet. */
export const OptomechOpenInterfaceSchema = z.record(z.string(), z.unknown());
export type OptomechOpenInterface = z.infer<typeof OptomechOpenInterfaceSchema>;

/**
 * What a part IS, by family: the family and the typed dimension record that
 * family's generator and fit rules read. The one discriminated union every
 * consumer narrows on (`part.family === 'lens_tube'` types `part.interface`),
 * in place of a family switch that re-parses an untyped record.
 */
export type OptomechPartSpec =
  | { family: 'lens_tube'; interface: OptomechLensTubeInterface }
  | { family: 'retaining_ring'; interface: OptomechRetainingRingInterface }
  | { family: 'spacer_ring'; interface: OptomechSpacerRingInterface }
  | { family: 'cell' | 'adapter' | 'other'; interface: OptomechOpenInterface };

/** A part version by its stable id and version: how a composition, a build and a view name one. Everything else about it is read from the row. */
export const OptomechPartRefSchema = z.object({ part_id: z.string().uuid(), version: z.number().int().positive() }).strict();
export type OptomechPartRef = z.infer<typeof OptomechPartRefSchema>;

/**
 * One annulus of a part's solid of revolution in the ASSEMBLY frame (the first
 * composed lens's front vertex at z = 0, light toward +z, the frame the layout
 * rule and the build share): the material between `r_inner_mm` and
 * `r_outer_mm` over `z0_mm..z1_mm`. Every generated family (fea/parts.py) is a
 * stack of coaxial cylinders, so its cross-section is exactly a list of these
 * and its 3D form is their revolution; nothing is approximated.
 */
export const OptomechAnnulusSchema = z.object({
  z0_mm: z.number(),
  z1_mm: z.number(),
  r_inner_mm: z.number().nonnegative(),
  r_outer_mm: z.number().positive(),
}).strict().refine((a) => a.z0_mm < a.z1_mm && a.r_inner_mm < a.r_outer_mm, { message: 'an annulus has positive length and positive wall' });
export type OptomechAnnulus = z.infer<typeof OptomechAnnulusSchema>;

const annulus = (z0: number, z1: number, rInner: number, rOuter: number): OptomechAnnulus => ({ z0_mm: z0, z1_mm: z1, r_inner_mm: rInner, r_outer_mm: rOuter });

/** The role the stack places a part in (spec 2026-10-19 §6.2). */
export const OPTOMECH_STAGING_ROLES = ['tube', 'ring'] as const;
export type OptomechStagingRole = typeof OPTOMECH_STAGING_ROLES[number];

/** The two headline numbers of a row and its thread: what the catalog's range filters, the ranking and the parts table read. */
export interface OptomechPartSummary {
  /** The bore a lens enters: a tube's bore; a ring's or a spacer's outside, the bore it rides in. */
  bore_mm: number;
  /** The length along the axis: a ring's thickness. */
  length_mm: number;
  /** The thread standard the part is cut to; null for a family without one (a spacer rides free). */
  thread: string | null;
}

/**
 * What one generated family's record gives, in one place: the stack role it
 * can take, what its length is called, its headline numbers, and its solid of
 * revolution — the family's `plan()` in fea/parts.py mirrored operation for
 * operation, in the part's own frame (origin at the REAR face, solid in
 * [-length, 0]). Backend and frontend read this table through `partRule`; no
 * consumer switches on the family itself.
 */
export interface OptomechFamilyRule<I> {
  /** Null for a family the stack does not place yet (a spacer sits between two lenses, C2). */
  staging_role: OptomechStagingRole | null;
  length_word: 'length' | 'thickness';
  summary(record: I): OptomechPartSummary;
  annuli(record: I): OptomechAnnulus[];
}

/**
 * LensTube.plan(): the outer cylinder less the bore over the whole length; an
 * external thread replaces the outer diameter by the stub's over the stub's
 * length at its end; a shoulder is a ring of the tube's own body left in the
 * bore. Written as disjoint annuli so the drawn section carries no overlap.
 */
function lensTubeAnnuli(t: OptomechLensTubeInterface): OptomechAnnulus[] {
  const bore = t.bore_mm / 2;
  const outer = t.outer_diameter_mm / 2;
  const length = t.length_mm;
  const out: OptomechAnnulus[] = [];
  if (t.external_thread === undefined) {
    out.push(annulus(-length, 0, bore, outer));
  } else {
    const stub = t.external_thread.diameter_mm / 2;
    const stubLength = t.external_thread.length_mm;
    if (t.external_thread.end === 'rear') {
      out.push(annulus(-length, -stubLength, bore, outer), annulus(-stubLength, 0, bore, stub));
    } else {
      out.push(annulus(-length, -length + stubLength, bore, stub), annulus(-length + stubLength, 0, bore, outer));
    }
  }
  if (t.shoulder !== undefined) {
    const s = t.shoulder;
    out.push(annulus(-(s.from_rear_mm + s.thickness_mm), -s.from_rear_mm, s.inner_diameter_mm / 2, bore));
  }
  return out;
}

export const OPTOMECH_FAMILY_RULE: {
  lens_tube: OptomechFamilyRule<OptomechLensTubeInterface>;
  retaining_ring: OptomechFamilyRule<OptomechRetainingRingInterface>;
  spacer_ring: OptomechFamilyRule<OptomechSpacerRingInterface>;
} = {
  lens_tube: {
    staging_role: 'tube',
    length_word: 'length',
    summary: (t) => ({ bore_mm: t.bore_mm, length_mm: t.length_mm, thread: t.thread }),
    annuli: lensTubeAnnuli,
  },
  retaining_ring: {
    staging_role: 'ring',
    length_word: 'thickness',
    summary: (r) => ({ bore_mm: r.outer_diameter_mm, length_mm: r.thickness_mm, thread: r.thread }),
    annuli: (r) => [annulus(-r.thickness_mm, 0, r.clear_aperture_mm / 2, r.outer_diameter_mm / 2)],
  },
  spacer_ring: {
    staging_role: null,
    length_word: 'length',
    summary: (s) => ({ bore_mm: s.outer_diameter_mm, length_mm: s.length_mm, thread: null }),
    annuli: (s) => [annulus(-s.length_mm, 0, s.inner_diameter_mm / 2, s.outer_diameter_mm / 2)],
  },
};

/** A generated family's rule bound to one part's record: its role, its length word, and its summary and annuli on demand. */
export interface OptomechPartRule {
  family: OptomechGeneratedFamily;
  staging_role: OptomechStagingRole | null;
  length_word: 'length' | 'thickness';
  summary(): OptomechPartSummary;
  annuli(): OptomechAnnulus[];
}

const bind = <I>(family: OptomechGeneratedFamily, rule: OptomechFamilyRule<I>, record: I): OptomechPartRule => ({
  family, staging_role: rule.staging_role, length_word: rule.length_word, summary: () => rule.summary(record), annuli: () => rule.annuli(record),
});

/** The one dispatch on a part's family: its generated family's rule on its record, or null for a family without a generator. */
export function partRule(part: OptomechPartSpec): OptomechPartRule | null {
  switch (part.family) {
    case 'lens_tube': return bind(part.family, OPTOMECH_FAMILY_RULE.lens_tube, part.interface);
    case 'retaining_ring': return bind(part.family, OPTOMECH_FAMILY_RULE.retaining_ring, part.interface);
    case 'spacer_ring': return bind(part.family, OPTOMECH_FAMILY_RULE.spacer_ring, part.interface);
    case 'cell':
    case 'adapter':
    case 'other':
      return null;
  }
}

/** How a row's solid comes to be. A stored-STEP kind is reserved (spec §11) and not accepted yet. */
export const OptomechPartGeometrySchema = z.object({
  kind: z.literal('parametric'),
  /** The generator (fea-service/fea/parts.py) the row is built by: its family's. */
  generator: z.enum(OPTOMECH_GENERATED_FAMILIES),
  /** That generator's version when the row was written (`GENERATOR_VERSIONS` there); a change to what a generator does is a new version, never an edit of the old one. */
  generator_version: z.string().regex(SEMVER_RE),
}).strict();
export type OptomechPartGeometry = z.infer<typeof OptomechPartGeometrySchema>;

// The citation on a dimension is the one record every sourced number carries: OptomechCitationSchema (optomechMaterials.ts).

export const OPTOMECH_PART_BODY_ROLES = ['structural', 'bonded_insert'] as const;

/** One solid of a part version, with the material it is made of by stable id (spec rule 6). */
export const OptomechPartBodySchema = z.object({
  body_name: z.string().regex(/^[A-Za-z0-9_-]{1,60}$/),
  role: z.enum(OPTOMECH_PART_BODY_ROLES),
  material_id: z.string().uuid(),
  generator_body: z.string().regex(/^[A-Za-z0-9_-]{1,60}$/),
});
export type OptomechPartBody = z.infer<typeof OptomechPartBodySchema>;

/**
 * One part VERSION as the API serves it, with its bodies. `family` and
 * `interface` are the row's OptomechPartSpec: on the wire `interface` stays the
 * open record it always was (the contract gate reads any reshaping of the
 * property — a union, a dropped `propertyNames` — as breaking), and the refine
 * parses it as the family's typed record, so a row whose record is not its
 * family's is not a row; the TypeScript type carries the union the refine
 * guarantees.
 */
const partRowShape = {
  /** The version id: what parts_used names. */
  id: z.string().uuid(),
  /** Stable across versions: what a composition names. */
  part_id: z.string().uuid(),
  version: z.number().int().positive(),
  scope: OptomechPartScopeSchema,
  project_id: z.string().uuid().nullable(),
  vendor: z.string(),
  part_number: z.string(),
  revision: z.string(),
  name: z.string(),
  family: OptomechPartFamilySchema,
  interface: OptomechOpenInterfaceSchema,
  /** Keyed by the dotted field path in `interface` (`bore_mm`, `shoulder.inner_diameter_mm`). */
  citations: z.record(z.string(), OptomechCitationSchema),
  geometry: OptomechPartGeometrySchema,
  fingerprint: CadFingerprintSchema.nullable(),
  source_url: z.string().nullable(),
  drawing_url: z.string().nullable(),
  bodies: z.array(OptomechPartBodySchema),
  created_by: z.string(),
  created_at: z.string(),
  superseded_at: z.string().nullable(),
};

/** The spec's invariant on the wire's object: a generated family's `interface` parses as that family's record. */
function familyMatchesInterface(row: { family: OptomechPartFamily; interface: unknown }, ctx: z.RefinementCtx): void {
  if (!(row.family in OPTOMECH_FAMILY_INTERFACE)) return;
  const parsed = OPTOMECH_FAMILY_INTERFACE[row.family as OptomechGeneratedFamily].safeParse(row.interface);
  if (!parsed.success) ctx.addIssue({ code: 'custom', path: ['interface'], message: `not a ${row.family.replace('_', ' ')} record: ${parsed.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; ')}` });
}

const PartRowObject = z.object(partRowShape).superRefine(familyMatchesInterface);
export type OptomechPartRow = Omit<z.infer<typeof PartRowObject>, 'family' | 'interface'> & OptomechPartSpec;
// The refine makes the object's output the spec union, which zod cannot express on one object without reshaping the wire; the type says so.
export const OptomechPartRowSchema = PartRowObject as unknown as z.ZodType<OptomechPartRow, z.input<typeof PartRowObject>>;

export const OptomechThreadListSchema = z.object({ threads: z.array(OptomechThreadSchema) });

// --- fit (spec §5) -----------------------------------------------------------

export const OPTOMECH_FIT_VERDICTS = ['fits', 'fits_with_note', 'does_not_fit'] as const;
export const OptomechFitVerdictSchema = z.enum(OPTOMECH_FIT_VERDICTS);
export type OptomechFitVerdict = z.infer<typeof OptomechFitVerdictSchema>;

/**
 * One fit judgement: the verdict and the named facts it rests on
 * (`clearance_mm`, `thread_match`, `length_margin_mm`, …), with each reason a
 * verdict short of `fits` was given. Fit never selects; it only judges. The
 * clearance a lens rule used is always among the facts as
 * `diametral_clearance_mm` (the sum of both sides), never silent.
 */
export const OptomechFitResultSchema = z.object({
  verdict: OptomechFitVerdictSchema,
  facts: z.record(z.string(), z.union([z.number(), z.string(), z.boolean()])),
  reasons: z.array(z.string()),
});
export type OptomechFitResult = z.infer<typeof OptomechFitResultSchema>;

/** The lens a search judged parts against, as the fit rules saw it. */
export const OptomechFitTargetSchema = z.object({
  /** The element's first and last surface numbers in the current system: a singlet's front and rear, a cemented group's front and its last face. */
  surfaces: z.tuple([z.number().int(), z.number().int()]),
  /** Twice the semi-diameter all the element's surfaces share: the element's one edge diameter. */
  lens_diameter_mm: z.number().positive(),
  center_thickness_mm: z.number().positive(),
  edge_thickness_mm: z.number().positive(),
  /** The user's number (spec §11 Q3: the family defaults have no source yet). */
  diametral_clearance_mm: z.number().nonnegative(),
});
export type OptomechFitTarget = z.infer<typeof OptomechFitTargetSchema>;

/**
 * A part in a search result: the row, and when the search named a lens, its
 * verdict against that lens, its place in the ranking of the parts that hold
 * (spec §5: shortest length that holds; null for one that does not, and for a
 * family no rule judges against a lens) and the sentence that says why.
 */
const PartCandidateObject = z.object({
  ...partRowShape,
  fit: OptomechFitResultSchema.nullable(),
  rank: z.number().int().positive().nullable(),
  rationale: z.string().nullable(),
}).superRefine(familyMatchesInterface);
export type OptomechPartCandidate = OptomechPartRow & Pick<z.infer<typeof PartCandidateObject>, 'fit' | 'rank' | 'rationale'>;
export const OptomechPartCandidateSchema = PartCandidateObject as unknown as z.ZodType<OptomechPartCandidate, z.input<typeof PartCandidateObject>>;

/** GET /optomech/parts: the current global rows that pass the filters, judged and ranked when `fits_surface` was given. */
export const OptomechPartSearchSchema = z.object({
  parts: z.array(OptomechPartCandidateSchema),
  fit_target: OptomechFitTargetSchema.nullable(),
});
export type OptomechPartSearch = z.infer<typeof OptomechPartSearchSchema>;
